import { useCallback, useEffect, useState } from "react";

export type FontSize = "small" | "default" | "medium" | "large";

export const FONT_SIZE_STORAGE_KEY = "phazex_font_size";

export const FONT_SIZE_MAP: Record<FontSize, string> = {
  small: "13px",
  default: "14px",
  medium: "15px",
  large: "16px",
};

export const FONT_SIZES: FontSize[] = ["small", "default", "medium", "large"];

export function readStoredFontSize(): FontSize {
  if (typeof window === "undefined") return "default";
  try {
    const raw = window.localStorage.getItem(FONT_SIZE_STORAGE_KEY);
    if (
      raw === "small" ||
      raw === "default" ||
      raw === "medium" ||
      raw === "large"
    ) {
      return raw;
    }
  } catch {
    // ignore
  }
  return "default";
}

export function applyFontSize(size: FontSize): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  for (const s of FONT_SIZES) {
    root.classList.remove(`font-${s}`);
  }
  root.classList.add(`font-${size}`);
  root.style.setProperty("--app-font-size", FONT_SIZE_MAP[size]);
}

const FONT_SIZE_EVENT = "phazex:font-size-change";

export function useFontSize() {
  const [size, setSizeState] = useState<FontSize>(() => readStoredFontSize());

  useEffect(() => {
    function onChange(e: Event) {
      const detail = (e as CustomEvent<FontSize>).detail;
      if (detail) setSizeState(detail);
    }
    window.addEventListener(FONT_SIZE_EVENT, onChange as EventListener);
    return () =>
      window.removeEventListener(FONT_SIZE_EVENT, onChange as EventListener);
  }, []);

  const setSize = useCallback((next: FontSize) => {
    setSizeState(next);
    applyFontSize(next);
    try {
      window.localStorage.setItem(FONT_SIZE_STORAGE_KEY, next);
    } catch {
      // ignore
    }
    window.dispatchEvent(
      new CustomEvent<FontSize>(FONT_SIZE_EVENT, { detail: next }),
    );
  }, []);

  return { size, setSize };
}
